import { isEmpty, indexOf } from 'lodash';
import { isElementVisibleWithinParent } from 'app/utils/helpers';
import { loadAsyncScript } from 'app/utils/ajaxHelpers';
import mediaQueries from '../../../utils/mediaQueries';
import {
  GRID_ORDER,
  GRID_TEMPLATES,
  LOAD_1_PRODUCT_PER_ROW,
  LOAD_2_PRODUCTS_PER_ROW,
  LOAD_3_PRODUCTS_PER_ROW,
} from './productListConstants';
import { getAppliedFacetsForApiQuery, computeUrlHashForCurrentOptions } from '../../productsReducer';

const { plyr } = window.inlineGlobalConfig.hashedPaths;
const { categoryId, categoryName, heroImageType, thumbnailImageType, variationField, query } = window.inlinePlp || {};

export const prepareQueryParams = (getState, offset, limit) => {
  const state = getState();
  const { selectedSort } = state;
  const selectedFacets = getAppliedFacetsForApiQuery(state);
  const resultObj = {
    categoryId,
    categoryName,
    offset,
    limit,
    heroImageType,
    thumbnailImageType,
    variationField,
    filter: JSON.stringify({
      q: query,
      facets: selectedFacets,
    }),
  };

  if (selectedSort !== '') {
    resultObj.sortField = selectedSort;
  }

  return resultObj;
};

const applyKeywordRedirect = json => {
  const { keywordRedirectUrl } = json;
  if (keywordRedirectUrl) {
    const redirect = new URL(window.location.origin + keywordRedirectUrl);
    const currentParams = new URL(document.location).searchParams;

    // eslint-disable-next-line no-restricted-syntax
    for (const [key, value] of currentParams.entries()) {
      if (key && !redirect.searchParams.has(key)) {
        redirect.searchParams.append(key, value);
      }
    }

    // prevent infinite loop, when keyword redirects to itself
    if (redirect.searchParams.get('query') === currentParams.get('query')) {
      redirect.searchParams.delete('query');
    }

    window.location.href = redirect.toString();
    return true;
  }
  return false;
};

const applyProductRedirect = json => {
  const { products } = json;
  if (products && products.length === 1) {
    const { pathname, hash } = new URL(window.location.origin + products[0].url);
    window.location.href = pathname + window.location.search + hash;
    return true;
  }
  return false;
};

const redirects = [applyKeywordRedirect, applyProductRedirect];

export const applyRedirects = json => {
  const redirectsLength = redirects.length;
  let isRedirected = false;
  let i = 0;
  for (i; i < redirectsLength; i += 1) {
    isRedirected = redirects[i](json);
    if (isRedirected) {
      break;
    }
  }
  return i !== redirectsLength;
};

const getQueryParamsToKeepAsString = () => {
  const allUrlQueryParams = window.location.search
    .substr(1)
    .split('&')
    .map(param => param.split('='));

  const keepQueryParams = [
    'query',
    'referrer',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_id',
    'uid',
    'ranMID',
    'ranEAID',
    'ranSiteID',
  ];

  let queryString;
  allUrlQueryParams.forEach(paramPair => {
    const [key, value] = paramPair;
    if (indexOf(keepQueryParams, key) >= 0) {
      queryString = `${queryString ? `${queryString}&` : ''}${key}=${value}`;
    }
  });
  return queryString;
};

export const setQueryInUrl = appState => {
  const facetsAndSortAsString = computeUrlHashForCurrentOptions(appState);
  const queryParamsToKeepString = getQueryParamsToKeepAsString();
  const computedQueryString =
    `${queryParamsToKeepString || ''}${facetsAndSortAsString && `&${facetsAndSortAsString}`}` || '';

  const currentUrlWithoutQueryString = window.location.href.replace(window.location.search, '');

  window.history.replaceState(
    {},
    document.title,
    isEmpty(computedQueryString)
      ? currentUrlWithoutQueryString
      : `${currentUrlWithoutQueryString}?${computedQueryString}`
  );
};

// TODO - remove isGridView
// eslint-disable-next-line no-unused-vars
export const gridColumnsToShow = isGridView => {
  const isTabletDown = mediaQueries.is_tablet_down();
  const isTabletUp = mediaQueries.is_tablet_up();

  if (isTabletUp) {
    return LOAD_3_PRODUCTS_PER_ROW;
  }
  if (isTabletDown) {
    return LOAD_2_PRODUCTS_PER_ROW;
  }
  return LOAD_1_PRODUCT_PER_ROW;
};

export const addContentSpotsToProductsGrid = (contentSpots, grid) => {
  const productsGrid = grid;

  contentSpots.forEach(contentSpot => {
    const { row, alignment, format } = contentSpot;
    Object.keys(productsGrid).forEach(gridKey => {
      if (Object.prototype.hasOwnProperty.call(productsGrid, gridKey)) {
        productsGrid[gridKey][row - 1] = Object.assign(
          {
            row: row - 1,
            contentSpot,
            order: GRID_ORDER[alignment],
            products: [],
            complete: false,
          },
          GRID_TEMPLATES[format][gridKey]
        );
      }
    });
  });

  // Fill empty slots in productsGrid with undefined
  Object.keys(productsGrid).forEach(gridKey => {
    if (Object.prototype.hasOwnProperty.call(productsGrid, gridKey)) {
      productsGrid[gridKey] = Array.from(productsGrid[gridKey]);
    }
  });

  return productsGrid;
};

export const addProductsToProductGrid = (allProducts, grid, isListingPage = false) => {
  const productsGrid = grid;

  const getProductIndex = (array, item) => {
    for (let i = 0; i < array.length; i++) {
      if (array[i].id === item.id) {
        return i;
      }
    }
    return -1;
  };

  const itemsToProcess = isListingPage ? allProducts.map(p => p.item || p) : allProducts;

  Object.keys(productsGrid).forEach(gridKey => {
    if (Object.prototype.hasOwnProperty.call(productsGrid, gridKey)) {
      const productsPerGrid = [].concat(itemsToProcess);
      const currentProductsGrid = [].concat(productsGrid[gridKey]);

      if (isEmpty(currentProductsGrid)) {
        currentProductsGrid[0] = Object.assign({}, GRID_TEMPLATES.standard[gridKey], { row: 0 });
      }

      currentProductsGrid.slice().forEach((gridRow, gridRowKey) => {
        const addTemplateToRow = (row, rowKey) => {
          if (isEmpty(row)) {
            currentProductsGrid[rowKey] = Object.assign({}, GRID_TEMPLATES.standard[gridKey], { row: rowKey });
            addTemplateToRow(currentProductsGrid[rowKey], rowKey);
          }
        };
        addTemplateToRow(gridRow, gridRowKey);

        const addProductsToRow = (row, rowKey, remainProductsPerGrid) => {
          if (!row.complete && remainProductsPerGrid.length) {
            const item = remainProductsPerGrid.shift();

            const slotsToConsume = isListingPage && item.type === 'advert' && item.size === 2 ? 2 : 1;
            const availableSlots = row.availableSlots - slotsToConsume;

            const products = row.products.concat(
              Object.assign({}, item, {
                resultPosition: getProductIndex(allProducts, item),
              })
            );
            currentProductsGrid[rowKey] = Object.assign(currentProductsGrid[rowKey], {
              availableSlots,
              complete: !availableSlots,
              products,
            });
            addProductsToRow(currentProductsGrid[rowKey], rowKey, remainProductsPerGrid);
          }
        };
        addProductsToRow(currentProductsGrid[gridRowKey], gridRowKey, productsPerGrid);

        if (gridRowKey + 1 === currentProductsGrid.length && productsPerGrid) {
          const remainRows = Math.ceil(productsPerGrid.length / parseInt(gridKey, 10));
          for (let i = gridRowKey + 1; i <= gridRowKey + remainRows; i++) {
            currentProductsGrid.push(Object.assign({}, GRID_TEMPLATES.standard[gridKey], { row: i }));
            addProductsToRow(currentProductsGrid[i], i, productsPerGrid);
          }
        }
      });

      currentProductsGrid.slice().forEach((row, rowKey) => {
        const { products, contentSpot, complete } = row;
        if ((isEmpty(products) && contentSpot === null) || (isEmpty(products) && contentSpot !== null && !complete)) {
          currentProductsGrid.splice(rowKey, currentProductsGrid.length);
        }
      });

      productsGrid[gridKey] = [].concat(currentProductsGrid);
    }
  });

  return productsGrid;
};

export const customizeContentSpots = contentSpotRefs => {
  const MEDIA_BANNER_CLASS = 'media-banner-component';
  const BUTTONS_BANNER_CLASS = 'buttons-banner-component';
  const VIDEO_BANNER_CLASS = 'video-banner-component';

  const getMediaTypeOf = element => {
    if (element.getElementsByClassName(MEDIA_BANNER_CLASS).length) {
      return MEDIA_BANNER_CLASS;
    }
    if (element.getElementsByClassName(BUTTONS_BANNER_CLASS).length) {
      return BUTTONS_BANNER_CLASS;
    }
    if (element.getElementsByClassName(VIDEO_BANNER_CLASS).length) {
      return VIDEO_BANNER_CLASS;
    }
    return null;
  };

  const setMediaRatioFor = (id, ratio) => {
    const aspectRatioRuleIndex = 0;
    const styleSheet = Array.from(document.styleSheets).find(stylesheet => stylesheet.ownerNode.dataset.id === id);
    if (styleSheet) {
      styleSheet.deleteRule(aspectRatioRuleIndex);
      styleSheet.insertRule(`#${id}:before { padding-top: ${ratio}%; }`, aspectRatioRuleIndex);
    }
  };

  const calculateAspectRatioFor = ({ banner, className, contentSpotRef }) => {
    const PERCENTAGE_100 = 100;

    const bannerId = banner.id;
    const bannerWrapper = banner.querySelector('.media-banner__wrapper');
    const bannerWidth = bannerWrapper.getBoundingClientRect().width;
    const bannerHeight = (
      className === BUTTONS_BANNER_CLASS ? banner.querySelector('.media-buttons-banner__copy') : bannerWrapper
    ).getBoundingClientRect().height;

    const siblingColumn = [...contentSpotRef.parentNode.children].find(
      sibling => sibling.nodeType === 1 && sibling !== contentSpotRef
    );
    const siblingColumnHeight = siblingColumn
      ? siblingColumn.querySelector('.product-list-grid__container').getBoundingClientRect().height
      : 0;
    const siblingColumnMargin = siblingColumn
      ? parseInt(window.getComputedStyle(siblingColumn.querySelector('.col')).getPropertyValue('margin-bottom'), 10)
      : 0;

    const aspectRatio = (
      ((siblingColumnHeight ? siblingColumnHeight - siblingColumnMargin : bannerHeight) / bannerWidth) *
      PERCENTAGE_100
    ).toFixed(2);
    return bannerWidth ? setMediaRatioFor(bannerId, aspectRatio) : false;
  };

  const initializePlayer = videoBanner => {
    const videoId = videoBanner.id;
    const videoElement = videoBanner.querySelector('video');
    const videoOptionsDataset = videoBanner.dataset.videoOptions;
    const videoOptions = videoOptionsDataset
      ? JSON.parse(videoOptionsDataset.replace(/&quot;/g, '"').replace(/'/g, '"'))
      : {};

    const init = (id, element, options) => {
      const autoplay = options.autoplay && isElementVisibleWithinParent(videoElement);
      window.initializedVideosInContentSpots = window.initializedVideosInContentSpots || {};
      window.initializedVideosInContentSpots[id] = {
        player: new window.Plyr(element, { ...options, autoplay }),
      };
    };

    return window.Plyr
      ? init(videoId, videoElement, videoOptions)
      : loadAsyncScript(plyr).then(() => init(videoId, videoElement, videoOptions));
  };

  contentSpotRefs.forEach(contentSpotRef => {
    const contentSpotType = getMediaTypeOf(contentSpotRef);

    const contentSpotsMap = {
      [MEDIA_BANNER_CLASS]() {
        [...contentSpotRef.getElementsByClassName('media-banner__ratio')].forEach(mediaBanner => {
          calculateAspectRatioFor({ banner: mediaBanner, contentSpotRef });
          const videoBannerInMedia = mediaBanner.querySelector('.video-banner--in-media');
          if (videoBannerInMedia) {
            initializePlayer(videoBannerInMedia);
          }
        });
      },
      [BUTTONS_BANNER_CLASS](className) {
        [...contentSpotRef.getElementsByClassName('media-banner__ratio')].forEach(buttonsBanner => {
          calculateAspectRatioFor({
            banner: buttonsBanner,
            className,
            contentSpotRef,
          });
          buttonsBanner.classList.remove('hidden');
        });
      },
      [VIDEO_BANNER_CLASS]() {
        const videoBanner = contentSpotRef.querySelector('.video-banner');
        initializePlayer(videoBanner);
      },
    };

    if (contentSpotType) {
      contentSpotsMap[contentSpotType](contentSpotType);
    }
  });
};

export const handleInfiniteScrollObserver = (productsRefs, onInfiniteScrollIntersection) => {
  let infiniteObserver; // eslint-disable-line
  const lastImageInProductGrid = productsRefs[productsRefs.length - 1].querySelector('picture');

  const onLastImageIntersection = entry => {
    const lastImage = entry[0];
    if (lastImage.isIntersecting) {
      onInfiniteScrollIntersection();
      infiniteObserver.unobserve(lastImage.target);
      lastImage.target.classList.remove('infinite-observer');
    }
  };

  if (!lastImageInProductGrid.classList.contains('infinite-observer')) {
    const observerConfig = {
      rootMargin: '750px 0px',
      threshold: 0.1,
    };
    infiniteObserver = new IntersectionObserver(onLastImageIntersection, observerConfig);

    lastImageInProductGrid.classList.add('infinite-observer');
    infiniteObserver.observe(lastImageInProductGrid);
  }
};

export const setPortraitClass = (domElement, className) => {
  const isPortraitOrientation = mediaQueries.is_portrait();
  if (isPortraitOrientation) {
    domElement.classList.add(className);
  } else if (!isPortraitOrientation) {
    domElement.classList.remove(className);
  }
};

export const checkScrollSpeed = (() => {
  const delay = 50; // in "ms" (higher means lower fidelity )
  let lastPos;
  let newPos;
  let timer;
  let delta;

  function clear() {
    lastPos = null;
    delta = 0;
  }

  clear();

  return () => {
    newPos = window.scrollY;
    if (lastPos != null) {
      delta = newPos - lastPos;
    }
    lastPos = newPos;
    clearTimeout(timer);
    timer = setTimeout(clear, delay);
    return Math.abs(delta);
  };
})();

export const inWishlistProducts = (wishlist, products) => {
  const wishlistEntries = wishlist.entries ? wishlist.entries.map(product => (product.baseId || '').toLowerCase()) : [];
  return products.reduce(
    (acc, cur) =>
      acc.concat([
        {
          ...cur,
          inWishlist: wishlistEntries.includes((cur.id || '').toLowerCase()),
        },
      ]),
    []
  );
};

const utils = {
  applyKeywordRedirect,
  applyRedirects,
  prepareQueryParams,
};

export const takeProducts = (sourceObj, key, productCount) => {
  const items = sourceObj[key] || [];

  return items.reduce((result, item) => {
    if (productCount <= 0 || !item.products) return result;

    const productsToTake = item.products.slice(0, productCount);

    result.push({ ...item, products: productsToTake });

    productCount -= productsToTake.length;

    return result;
  }, []);
};

export default utils;
